import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Pencil, CalendarDays, Hash, DollarSign, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getLocalDateString } from '@/lib/utils';
import api from '@/lib/api';
import { HelpTip } from '@/components/HelpTip';

const SupplierDeadlines = () => {
  const [data, setData] = useState({ items: [], total: 0, count: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [nextPaymentDate, setNextPaymentDate] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const loadNextPaymentDate = useCallback(async () => {
    try {
      const response = await api.get('/user-settings/next_payment_date');
      if (response.data.value) {
        setNextPaymentDate(response.data.value);
      } else {
        setNextPaymentDate(getLocalDateString());
      }
    } catch {
      setNextPaymentDate(getLocalDateString());
    }
  }, []);

  const saveNextPaymentDate = async (date) => {
    setNextPaymentDate(date);
    try {
      await api.put('/user-settings/next_payment_date', { value: date });
    } catch {
      // silent
    }
  };
  const [formData, setFormData] = useState({
    amount: '', category_id: '', description: '', date: getLocalDateString(), deadline_days: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category_id = filterCategory;
      const response = await api.get('/supplier-deadlines', { params });
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get('/categories?type=saida');
      setCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  }, []);

  useEffect(() => { loadData(); loadCategories(); loadNextPaymentDate(); }, [loadData, loadCategories, loadNextPaymentDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        deadline_days: formData.deadline_days ? parseInt(formData.deadline_days, 10) : null,
      };
      if (editingItem) {
        await api.put(`/supplier-deadlines/${editingItem.id}`, payload);
        toast.success('Registro atualizado!');
      } else {
        await api.post('/supplier-deadlines', payload);
        toast.success('Registro criado!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar registro');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este registro?')) return;
    try {
      await api.delete(`/supplier-deadlines/${id}`);
      toast.success('Registro excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleClearAll = async () => {
    try {
      await api.delete('/supplier-deadlines');
      toast.success('Todos os registros foram zerados!');
      setShowClearConfirm(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao zerar registros');
    }
  };

  const resetForm = () => {
    setFormData({ amount: '', category_id: '', description: '', date: getLocalDateString(), deadline_days: '' });
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      amount: item.amount?.toString() || '',
      category_id: item.category_id || '',
      description: item.description || '',
      date: item.date || getLocalDateString(),
      deadline_days: item.deadline_days?.toString() || '',
    });
    setIsDialogOpen(true);
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';

  return (
    <div data-testid="supplier-deadlines-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Prazo Fornecedores
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Controle de valores pendentes para fornecedores
          <HelpTip text="Use esta página para anotar valores que você precisa pagar a fornecedores. Não afeta seus cálculos de entradas e saídas." className="ml-1" />
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Card data-testid="card-next-payment" className="overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Próximo Pagamento</CardTitle>
            <div className="flex items-center gap-1">
              <HelpTip text="Data do próximo pagamento previsto para fornecedores. Ajuste conforme necessário." />
              <CalendarDays className="text-indigo-600" size={18} />
            </div>
          </CardHeader>
          <CardContent className="overflow-hidden">
            {nextPaymentDate && (
              <div className="mb-3">
                <span className="text-xl sm:text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {formatDate(nextPaymentDate)}
                </span>
              </div>
            )}
            <Input
              type="date"
              value={nextPaymentDate}
              onChange={(e) => saveNextPaymentDate(e.target.value)}
              data-testid="next-payment-date"
              className="text-sm text-muted-foreground w-full max-w-full box-border"
            />
          </CardContent>
        </Card>

        <Card data-testid="card-total">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Total Acumulado</CardTitle>
            <div className="flex items-center gap-1">
              <HelpTip text="Soma de todos os valores pendentes cadastrados nesta página." />
              <DollarSign className="text-rose-600" size={18} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-rose-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatCurrency(data.total)}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-count">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Quantidade</CardTitle>
            <Hash className="text-indigo-600" size={18} />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.count}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Actions */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
        <Select value={filterCategory || 'all'} onValueChange={val => setFilterCategory(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category">
            <SelectValue placeholder="Todas categorias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-deadline-button" className="bg-indigo-600 hover:bg-indigo-700">
              <Plus size={18} className="mr-1.5" />Nova Transação
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="deadline-dialog">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar' : 'Nova'} Transação</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Valor (R$)</Label>
                <Input type="number" step="0.01" data-testid="deadline-amount" value={formData.amount}
                  onChange={e => setFormData({ ...formData, amount: e.target.value })} required placeholder="0,00" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={formData.category_id || 'none'} onValueChange={val => setFormData({ ...formData, category_id: val === 'none' ? '' : val })}>
                  <SelectTrigger data-testid="deadline-category"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem categoria</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data</Label>
                <Input type="date" data-testid="deadline-date" value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <Label>Prazo de Vencimento (dias)</Label>
                <Input type="number" min="1" step="1" data-testid="deadline-days" value={formData.deadline_days}
                  onChange={e => setFormData({ ...formData, deadline_days: e.target.value.replace(/\D/g, '') })}
                  placeholder="Ex: 7, 15, 30" />
              </div>
              <div>
                <Label>Descrição (opcional)</Label>
                <Input data-testid="deadline-description" value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Fornecedor WecaPrint" />
              </div>
              <Button type="submit" data-testid="deadline-submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {editingItem ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {data.count > 0 && (
          <Button variant="outline" onClick={() => setShowClearConfirm(true)} data-testid="clear-all-button"
            className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 ml-auto">
            <Trash2 size={16} className="mr-1.5" />Zerar
          </Button>
        )}
      </div>

      {/* Clear Confirmation Dialog */}
      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent data-testid="clear-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-rose-500" size={20} />
              Zerar Prazo Fornecedores
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Deseja realmente zerar todas as transações de Prazo Fornecedores?
          </p>
          <p className="text-sm text-muted-foreground">
            Esta ação não afeta entradas, saídas, produtos ou qualquer outro dado do sistema.
          </p>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setShowClearConfirm(false)}>Cancelar</Button>
            <Button onClick={handleClearAll} data-testid="confirm-clear-button"
              className="bg-rose-600 hover:bg-rose-700 text-white">Confirmar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Carregando...</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">Nenhum registro de prazo</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className="text-left p-3 font-medium text-muted-foreground">Data</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Categoria</th>
                <th className="text-left p-3 font-medium text-muted-foreground hidden sm:table-cell">Descrição</th>
                <th className="text-left p-3 font-medium text-muted-foreground hidden sm:table-cell">Vencimento</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Valor</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item.id} className="border-b border-border hover:bg-accent transition-colors">
                  <td className="p-3 text-foreground">{formatDate(item.date)}</td>
                  <td className="p-3 text-foreground">{getCategoryName(item.category_id)}</td>
                  <td className="p-3 text-muted-foreground hidden sm:table-cell">{item.description || '-'}</td>
                  <td className="p-3 hidden sm:table-cell">
                    {item.due_date ? (
                      <span className="text-amber-600 font-medium">{formatDate(item.due_date)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-3 text-right font-semibold text-rose-600">{formatCurrency(item.amount)}</td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEditDialog(item)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupplierDeadlines;
